/**
 * Administração → WhatsBI Cockpit: o WhatsApp da concessionária e quem pode perguntar por
 * ele (ADR-043).
 *
 * O cockpit não é um broker da Meta: o número é da concessionária, homologado por ela, e o
 * app na Meta também. Esta tela é o lado do cockpit dessa configuração — os três dados que
 * vêm da Meta (o id do número, o token de acesso e o segredo do app), a URL do webhook e o
 * token de verificação que se colam no app da Meta, e os celulares de quem pode perguntar.
 *
 * O token e o segredo **não voltam** da API: a tela diz só se estão guardados. Campo vazio
 * ao gravar mantém o que está lá.
 *
 * O cartão **Alertas** (`W-08`, ADR-052) guarda o modelo de mensagem aprovado na Meta para o
 * cockpit escrever primeiro, e a lista de celulares mostra os alertas que cada pessoa ligou.
 *
 * Na Administração, o WhatsBI são três seções (a revisão de 06/10, para nada ficar solto):
 * **WhatsApp e celulares** (`WhatsAppECelulares`, aqui), **Modelo e consumo**
 * (`ModeloEConsumo`: a chave da OpenAI da concessionária, o orçamento e o consumo do mês —
 * ADR-044, em `ModeloDoWhatsBi.tsx`) e **Conversas** (`ConversasDoWhatsBi.tsx`: quem
 * perguntou o quê, com o prazo de guarda — `Guarda`, daqui, `W-09`, ADR-048).
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { ErroDaApi, api } from "../api/cliente";
import type {
  CelularAutorizado,
  GuardaDasConversas,
  UsuarioDaOrganizacao,
  WhatsAppDaOrganizacao,
  WhatsAppDaOrganizacaoNovo,
} from "../api/tipos";
import { Badge, Botao, Cartao, Estado, Painel } from "../componentes/base";
import { Icone } from "../componentes/icones";
import { dataEHora } from "../formato";
import { ConsumoDoMes, ModeloDeLinguagem } from "./ModeloDoWhatsBi";

const ENTRADA =
  "min-h-8 w-full rounded-botao border border-linha bg-painel px-2 text-[11.5px] text-tinta";

function mensagem(falha: unknown): string {
  return falha instanceof ErroDaApi ? falha.message : "Falha inesperada.";
}

async function copiar(texto: string) {
  try {
    await navigator.clipboard.writeText(texto);
  } catch {
    // Sem permissão de área de transferência: o texto continua selecionável na tela.
  }
}

/** Administração → WhatsBI → Modelo e consumo. */
export function ModeloEConsumo() {
  // Gravar o modelo muda a chave em uso e o orçamento: o consumo relê.
  const [versaoDoModelo, definirVersaoDoModelo] = useState(0);
  return (
    <div className="grid gap-3">
      <ModeloDeLinguagem aoGravar={() => definirVersaoDoModelo((v) => v + 1)} />
      <ConsumoDoMes versao={versaoDoModelo} />
    </div>
  );
}

/** Administração → WhatsBI → WhatsApp e celulares. */
export function WhatsAppECelulares() {
  const [config, definirConfig] = useState<WhatsAppDaOrganizacao | null>(null);
  const [erro, definirErro] = useState<ErroDaApi | null>(null);

  const recarregar = useCallback(async () => {
    try {
      definirConfig(await api<WhatsAppDaOrganizacao>("/whatsbi/configuracao"));
      definirErro(null);
    } catch (falha) {
      definirErro(
        falha instanceof ErroDaApi
          ? falha
          : new ErroDaApi("Falha inesperada.", 0),
      );
    }
  }, []);

  useEffect(() => {
    void recarregar();
  }, [recarregar]);

  if (erro) {
    return (
      <Painel titulo="WhatsBI Cockpit">
        <Estado
          tipo={
            erro.semLicenca
              ? "sem-licenca"
              : erro.status === 403
                ? "bloqueado"
                : "erro"
          }
          titulo={
            erro.semLicenca
              ? "Esta organização não licenciou o WhatsBI Cockpit."
              : erro.status === 403
                ? "Esta área é do administrador da organização."
                : "Não foi possível carregar a configuração."
          }
          detalhe={erro.message}
        />
      </Painel>
    );
  }
  if (!config) {
    return (
      <Painel titulo="WhatsBI Cockpit">
        <Estado tipo="carregando" titulo="Carregando…" />
      </Painel>
    );
  }

  return (
    <div className="grid gap-3">
      <Painel
        titulo="O WhatsApp da concessionária"
        acoes={
          <Badge cor={config.ativo ? "verde" : "cinza"}>
            {config.ativo ? "Respondendo" : "Desligado"}
          </Badge>
        }
      >
        <div className="grid gap-3 md:grid-cols-2">
          <ComoLigar />
          <Formulario config={config} aoGravar={recarregar} />
          <Webhook config={config} />
          <ModeloDosAlertas config={config} aoGravar={recarregar} />
        </div>
      </Painel>
      <Celulares />
    </div>
  );
}

function ComoLigar() {
  return (
    <Cartao titulo="Como ligar" icone={<Icone.Conversa />}>
      <ol className="grid list-decimal gap-1.5 pl-4 text-[11.5px] text-tinta-2">
        <li>
          Na Meta (WhatsApp Business Platform), o número da concessionária
          homologado e um app com o produto WhatsApp.
        </li>
        <li>
          Do WhatsApp Manager e do app: o <b>id do número</b> (Phone number ID),
          um <b>token de acesso</b> permanente (de usuário do sistema) e o{" "}
          <b>segredo do app</b> (App secret). Cole os três aqui ao lado.
        </li>
        <li>
          No app da Meta, em Webhooks: cole a <b>URL</b> e o{" "}
          <b>token de verificação</b> abaixo e assine o campo{" "}
          <span className="mono">messages</span>.
        </li>
        <li>
          Cadastre os <b>celulares</b> de quem pode perguntar. Número que não
          está na lista não recebe resposta.
        </li>
        <li>
          Para os <b>alertas</b>, cadastre na Meta o modelo de mensagem do
          cartão <i>Alertas</i> e, aprovado, informe o nome dele. Cada pessoa
          liga os alertas dela no Pergunte ao Cockpit.
        </li>
      </ol>
      <p className="mt-2 text-[11px] text-tinta-3">
        Quem pergunta pelo WhatsApp vê o mesmo que veria na tela: o perfil e as
        concessões do usuário do celular.
      </p>
    </Cartao>
  );
}

function CampoDeTexto({
  rotulo,
  valor,
  aoMudar,
  placeholder,
  segredo,
}: {
  rotulo: string;
  valor: string;
  aoMudar: (v: string) => void;
  placeholder?: string | undefined;
  segredo?: boolean | undefined;
}) {
  return (
    <label className="grid gap-1 text-[11px] text-tinta-3">
      {rotulo}
      <input
        value={valor}
        aria-label={rotulo}
        type={segredo ? "password" : "text"}
        autoComplete="off"
        placeholder={placeholder}
        onChange={(e) => aoMudar(e.target.value)}
        className={ENTRADA}
      />
    </label>
  );
}

function Formulario({
  config,
  aoGravar,
}: {
  config: WhatsAppDaOrganizacao;
  aoGravar: () => Promise<void>;
}) {
  const [dados, definirDados] = useState({
    phone_number_id: config.phone_number_id ?? "",
    numero_exibido: config.numero_exibido ?? "",
    token_de_acesso: "",
    segredo_do_app: "",
  });
  const [gravando, definirGravando] = useState(false);
  const [aviso, definirAviso] = useState<{
    ok: boolean;
    texto: string;
    pendencias?: string[];
  } | null>(null);

  const mudar = (campo: keyof typeof dados) => (valor: string) =>
    definirDados((d) => ({ ...d, [campo]: valor }));

  async function gravar(ativo: boolean) {
    definirGravando(true);
    definirAviso(null);
    const corpo: WhatsAppDaOrganizacaoNovo = {
      ativo,
      phone_number_id: dados.phone_number_id.trim() || null,
      numero_exibido: dados.numero_exibido.trim() || null,
      token_de_acesso: dados.token_de_acesso.trim() || null,
      segredo_do_app: dados.segredo_do_app.trim() || null,
      // O modelo dos alertas tem o cartão dele; aqui vai como está, para não se perder.
      alerta_modelo: config.alerta_modelo ?? null,
      alerta_idioma: config.alerta_idioma,
    };
    try {
      await api("/whatsbi/configuracao", { method: "PUT", corpo });
      definirDados((d) => ({ ...d, token_de_acesso: "", segredo_do_app: "" }));
      definirAviso({
        ok: true,
        texto: ativo ? "Gravado. O WhatsApp está respondendo." : "Gravado.",
      });
      await aoGravar();
    } catch (falha) {
      const detalhe =
        falha instanceof ErroDaApi
          ? (falha.detalhe as { pendencias?: string[] })
          : undefined;
      definirAviso({
        ok: false,
        texto: mensagem(falha),
        ...(detalhe?.pendencias ? { pendencias: detalhe.pendencias } : {}),
      });
    } finally {
      definirGravando(false);
    }
  }

  return (
    <Cartao titulo="Dados da Meta" icone={<Icone.Chave />}>
      <div className="grid gap-2">
        <CampoDeTexto
          rotulo="Id do número (Phone number ID)"
          valor={dados.phone_number_id}
          aoMudar={mudar("phone_number_id")}
          placeholder="109876543210987"
        />
        <CampoDeTexto
          rotulo="Número exibido para a gestão"
          valor={dados.numero_exibido}
          aoMudar={mudar("numero_exibido")}
          placeholder="+55 61 3000-0000"
        />
        <CampoDeTexto
          rotulo="Token de acesso"
          valor={dados.token_de_acesso}
          aoMudar={mudar("token_de_acesso")}
          segredo
          placeholder={
            config.token_guardado ? "guardado — vazio mantém" : "cole aqui"
          }
        />
        <CampoDeTexto
          rotulo="Segredo do app (App secret)"
          valor={dados.segredo_do_app}
          aoMudar={mudar("segredo_do_app")}
          segredo
          placeholder={
            config.segredo_do_app_guardado
              ? "guardado — vazio mantém"
              : "cole aqui"
          }
        />
        {config.pendencias.length > 0 && (
          <ul
            aria-label="O que falta"
            className="grid gap-0.5 rounded-botao border border-amarelo-borda bg-amarelo-fundo px-2 py-1.5 text-[11px] text-amarelo"
          >
            {config.pendencias.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        )}
        <div className="flex flex-wrap items-center gap-1.5">
          <Botao
            papel="primaria"
            disabled={gravando}
            onClick={() => void gravar(true)}
          >
            Gravar e ligar
          </Botao>
          <Botao disabled={gravando} onClick={() => void gravar(false)}>
            {config.ativo ? "Desligar" : "Gravar desligado"}
          </Botao>
        </div>
        {aviso && (
          <div
            role={aviso.ok ? "status" : "alert"}
            className={`text-[11px] ${aviso.ok ? "text-verde" : "text-vermelho"}`}
          >
            {aviso.texto}
            {aviso.pendencias && (
              <ul className="mt-0.5 list-disc pl-4">
                {aviso.pendencias.map((p) => (
                  <li key={p}>{p}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </Cartao>
  );
}

function Webhook({ config }: { config: WhatsAppDaOrganizacao }) {
  const [mostrar, definirMostrar] = useState(false);
  const url = `${window.location.origin}${config.caminho_do_webhook}`;
  return (
    <Cartao titulo="Webhook (no app da Meta)" icone={<Icone.Recarga />}>
      <div className="grid gap-2 text-[11.5px]">
        <div>
          <p className="text-[11px] text-tinta-3">URL de callback</p>
          <p className="mono rounded-botao border border-linha bg-painel px-2 py-1.5 text-[11px] wrap-anywhere text-tinta">
            {url}
          </p>
        </div>
        <div>
          <p className="text-[11px] text-tinta-3">Token de verificação</p>
          <p className="mono rounded-botao border border-linha bg-painel px-2 py-1.5 text-[11px] text-tinta">
            {mostrar ? config.token_de_verificacao : "••••••••••••••••"}
          </p>
        </div>
        <div className="flex flex-wrap gap-1.5">
          <Botao onClick={() => void copiar(url)}>Copiar URL</Botao>
          <Botao onClick={() => definirMostrar((m) => !m)}>
            {mostrar ? "Ocultar token" : "Mostrar token"}
          </Botao>
          <Botao onClick={() => void copiar(config.token_de_verificacao)}>
            Copiar token
          </Botao>
        </div>
        <p className="text-[10.5px] text-tinta-3">
          Campo a assinar: <span className="mono">messages</span>. As entregas
          são conferidas pela assinatura do segredo do app.
        </p>
      </div>
    </Cartao>
  );
}

// --------------------------------------------------------------------------- alertas (`W-08`)

/** O nome de cada alerta, como a lista de celulares o mostra. */
const NOME_DO_ALERTA: Record<string, string> = {
  prazo_perdido: "prazo perdido",
  prazo_em_risco: "prazo em risco",
  indices_em_risco: "índices em risco",
  portico_parado: "pórtico parado",
  notificacao_em_falta: "prova da notificação",
  entrega_vencendo: "entregas ao poder concedente",
  senatran_fora_do_ar: "SENATRAN fora do ar",
};

/**
 * O modelo de mensagem dos alertas (`W-08`, ADR-052). Fora das 24 h depois da última
 * mensagem da pessoa, a Meta só deixa a empresa escrever com um modelo aprovado: o cartão
 * mostra o que cadastrar lá e guarda o nome dele aqui. Sem modelo, o alerta só chega a quem
 * escreveu ao cockpit nas últimas 24 h.
 */
function ModeloDosAlertas({
  config,
  aoGravar,
}: {
  config: WhatsAppDaOrganizacao;
  aoGravar: () => Promise<void>;
}) {
  const sugerido = config.modelo_sugerido;
  const [nome, definirNome] = useState(config.alerta_modelo ?? "");
  const [idioma, definirIdioma] = useState(config.alerta_idioma);
  const [gravando, definirGravando] = useState(false);
  const [aviso, definirAviso] = useState<{ ok: boolean; texto: string } | null>(
    null,
  );

  async function gravar() {
    definirGravando(true);
    definirAviso(null);
    const corpo: WhatsAppDaOrganizacaoNovo = {
      ativo: config.ativo,
      phone_number_id: config.phone_number_id ?? null,
      numero_exibido: config.numero_exibido ?? null,
      token_de_acesso: null,
      segredo_do_app: null,
      alerta_modelo: nome.trim() || null,
      alerta_idioma: idioma.trim() || "pt_BR",
    };
    try {
      await api("/whatsbi/configuracao", { method: "PUT", corpo });
      definirAviso({
        ok: true,
        texto: nome.trim()
          ? "Gravado. Os alertas saem pelo modelo."
          : "Gravado sem modelo: o alerta só chega a quem escreveu nas últimas 24 h.",
      });
      await aoGravar();
    } catch (falha) {
      definirAviso({ ok: false, texto: mensagem(falha) });
    } finally {
      definirGravando(false);
    }
  }

  return (
    <Cartao titulo="Alertas: o modelo de mensagem" icone={<Icone.Alerta />}>
      <div className="grid gap-2 text-[11.5px]">
        <p className="text-[11px] text-tinta-3">
          Para o cockpit escrever primeiro, a Meta exige um modelo aprovado.
          Cadastre este no WhatsApp Manager (Modelos de mensagem) e, aprovado,
          informe o nome dele aqui.
        </p>
        <dl className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 text-[11px]">
          <dt className="text-tinta-3">Nome</dt>
          <dd className="mono text-tinta">{sugerido.nome}</dd>
          <dt className="text-tinta-3">Categoria</dt>
          <dd className="text-tinta">{sugerido.categoria}</dd>
          <dt className="text-tinta-3">Idioma</dt>
          <dd className="mono text-tinta">{sugerido.idioma}</dd>
        </dl>
        <pre
          aria-label="Corpo do modelo"
          className="mono rounded-botao border border-linha bg-painel px-2 py-1.5 text-[11px] whitespace-pre-wrap text-tinta"
        >
          {sugerido.corpo}
        </pre>
        <div className="flex flex-wrap gap-1.5">
          <Botao onClick={() => void copiar(sugerido.corpo)}>
            Copiar o corpo
          </Botao>
        </div>
        <div className="grid gap-2 md:grid-cols-[2fr_1fr]">
          <CampoDeTexto
            rotulo="Nome do modelo aprovado"
            valor={nome}
            aoMudar={definirNome}
            placeholder="vazio: sem modelo"
          />
          <CampoDeTexto
            rotulo="Idioma"
            valor={idioma}
            aoMudar={definirIdioma}
          />
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <Botao
            papel="primaria"
            disabled={gravando}
            onClick={() => void gravar()}
          >
            Gravar modelo
          </Botao>
          <Badge cor={config.alerta_modelo ? "verde" : "amarelo"}>
            {config.alerta_modelo
              ? `Em uso: ${config.alerta_modelo}`
              : "Sem modelo: só na janela de 24 h"}
          </Badge>
        </div>
        {aviso && (
          <p
            role={aviso.ok ? "status" : "alert"}
            className={`text-[11px] ${aviso.ok ? "text-verde" : "text-vermelho"}`}
          >
            {aviso.texto}
          </p>
        )}
      </div>
    </Cartao>
  );
}

// --------------------------------------------------------------------------- celulares

function Celulares() {
  const [celulares, definirCelulares] = useState<CelularAutorizado[] | null>(
    null,
  );
  const [usuarios, definirUsuarios] = useState<UsuarioDaOrganizacao[]>([]);
  const [escolhido, definirEscolhido] = useState("");
  const [numero, definirNumero] = useState("");
  const [aviso, definirAviso] = useState<{ ok: boolean; texto: string } | null>(
    null,
  );

  // A lista que não chegou (`N258`): sem ela o painel ficava em "Carregando…" para sempre, com o erro
  // escrito lá embaixo do formulário.
  const [falhaNaLista, definirFalhaNaLista] = useState<string | null>(null);

  const jaCarregou = useRef(false);

  const recarregar = useCallback(async () => {
    try {
      definirCelulares(await api<CelularAutorizado[]>("/whatsbi/telefones"));
      jaCarregou.current = true;
      definirFalhaNaLista(null);
    } catch (falha) {
      // Com a lista na tela, a releitura que falha é um aviso; sem ela, é o estado do painel.
      if (jaCarregou.current)
        definirAviso({ ok: false, texto: mensagem(falha) });
      else definirFalhaNaLista(mensagem(falha));
    }
  }, []);

  useEffect(() => {
    void recarregar();
    api<UsuarioDaOrganizacao[]>("/admin/usuarios")
      // O regulador não pergunta pelo WhatsApp (`OB-13`): o servidor recusa o celular dele.
      .then((u) =>
        definirUsuarios(u.filter((x) => x.ativo && x.perfil !== "REGULADOR")),
      )
      .catch(() => undefined);
  }, [recarregar]);

  const comCelular = new Set((celulares ?? []).map((c) => c.usuario_id));
  const semCelular = usuarios.filter((u) => !comCelular.has(u.id));

  async function cadastrar() {
    definirAviso(null);
    try {
      await api("/whatsbi/telefones", {
        method: "POST",
        corpo: { usuario_id: escolhido, numero },
      });
      definirNumero("");
      definirEscolhido("");
      definirAviso({ ok: true, texto: "Celular cadastrado." });
      await recarregar();
    } catch (falha) {
      definirAviso({ ok: false, texto: mensagem(falha) });
    }
  }

  async function remover(c: CelularAutorizado) {
    if (
      !window.confirm(
        `${c.nome} deixa de poder perguntar pelo WhatsApp. Remover o celular?`,
      )
    )
      return;
    try {
      await api(`/whatsbi/telefones/${c.id}`, { method: "DELETE" });
      await recarregar();
    } catch (falha) {
      definirAviso({ ok: false, texto: mensagem(falha) });
    }
  }

  return (
    <Painel titulo="Quem pode perguntar pelo WhatsApp">
      <div className="grid gap-3">
        {celulares === null ? (
          falhaNaLista ? (
            <Estado
              tipo="erro"
              titulo="Não foi possível ler os celulares."
              detalhe={falhaNaLista}
              acao={
                <Botao onClick={() => void recarregar()}>Tentar de novo</Botao>
              }
            />
          ) : (
            <Estado tipo="carregando" titulo="Carregando…" />
          )
        ) : celulares.length === 0 ? (
          <p className="text-[11.5px] text-tinta-3">
            Nenhum celular cadastrado: o WhatsApp não responde a ninguém.
          </p>
        ) : (
          <ul aria-label="Celulares" className="grid gap-1.5">
            {celulares.map((c) => (
              <li
                key={c.id}
                className="flex flex-wrap items-center gap-2 rounded-[7px] border border-linha bg-painel-2 px-2.5 py-2 text-[11.5px]"
              >
                <span className="font-semibold text-tinta">{c.nome}</span>
                <span className="text-tinta-3">{c.email}</span>
                <span className="mono text-tinta-2">+{c.numero}</span>
                {!c.usuario_ativo && <Badge cor="cinza">usuário inativo</Badge>}
                <span className="text-[10.5px] text-tinta-3">
                  desde {dataEHora(c.criado_em)}
                </span>
                {(c.alertas ?? []).length > 0 && (
                  <span className="text-[10.5px] text-tinta-3">
                    alertas:{" "}
                    {(c.alertas ?? [])
                      .map((a) => NOME_DO_ALERTA[a] ?? a)
                      .join(", ")}
                  </span>
                )}
                <span className="ml-auto">
                  <Botao onClick={() => void remover(c)}>Remover</Botao>
                </span>
              </li>
            ))}
          </ul>
        )}
        <div className="grid gap-2 md:grid-cols-[1fr_1fr_auto] md:items-end">
          <label className="grid gap-1 text-[11px] text-tinta-3">
            Usuário
            <select
              aria-label="Usuário"
              value={escolhido}
              onChange={(e) => definirEscolhido(e.target.value)}
              className={ENTRADA}
            >
              <option value="">Escolha…</option>
              {semCelular.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.nome} ({u.perfil})
                </option>
              ))}
            </select>
          </label>
          <CampoDeTexto
            rotulo="Celular com DDD"
            valor={numero}
            aoMudar={definirNumero}
            placeholder="(61) 99999-0000"
          />
          <Botao
            papel="primaria"
            disabled={!escolhido || numero.trim().length < 8}
            onClick={() => void cadastrar()}
          >
            Cadastrar celular
          </Botao>
        </div>
        {aviso && (
          <p
            role={aviso.ok ? "status" : "alert"}
            className={`text-[11px] ${aviso.ok ? "text-verde" : "text-vermelho"}`}
          >
            {aviso.texto}
          </p>
        )}
      </div>
    </Painel>
  );
}

/**
 * O prazo de guarda das conversas (`W-09`, ADR-048). O administrador escolhe o prazo; quem
 * apaga é o expurgo do dono do banco, todo dia — e a tela mostra se ele está em dia: muita
 * mensagem além do prazo, por dias, é o expurgo que parou.
 */
export function Guarda() {
  const [guarda, definirGuarda] = useState<GuardaDasConversas | null>(null);
  const [dias, definirDias] = useState("");
  const [aviso, definirAviso] = useState<{ ok: boolean; texto: string } | null>(
    null,
  );

  const recarregar = useCallback(async () => {
    try {
      const lida = await api<GuardaDasConversas>("/whatsbi/guarda");
      definirGuarda(lida);
      definirDias(String(lida.guarda_dias));
    } catch (falha) {
      definirAviso({ ok: false, texto: mensagem(falha) });
    }
  }, []);

  useEffect(() => {
    void recarregar();
  }, [recarregar]);

  async function gravar() {
    definirAviso(null);
    try {
      const gravada = await api<GuardaDasConversas>("/whatsbi/guarda", {
        method: "PUT",
        corpo: { guarda_dias: Number(dias) },
      });
      definirGuarda(gravada);
      definirAviso({ ok: true, texto: "Prazo de guarda gravado." });
    } catch (falha) {
      definirAviso({ ok: false, texto: mensagem(falha) });
    }
  }

  const numero = Number(dias);
  const valido =
    guarda !== null &&
    Number.isInteger(numero) &&
    numero >= guarda.minimo &&
    numero <= guarda.maximo;

  return (
    <Painel
      titulo="Guarda das conversas"
      acoes={
        guarda &&
        (guarda.alem_do_prazo > 0 ? (
          <Badge cor="amarelo">esperando o expurgo</Badge>
        ) : (
          <Badge cor="verde">em dia</Badge>
        ))
      }
    >
      {guarda === null ? (
        aviso ? (
          <p role="alert" className="text-[11px] text-vermelho">
            {aviso.texto}
          </p>
        ) : (
          <Estado tipo="carregando" titulo="Carregando…" />
        )
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          <div className="grid content-start gap-2 text-[11.5px] text-tinta-2">
            <p>
              Perguntas e respostas do agente — na tela e no WhatsApp — ficam
              guardadas por{" "}
              <b className="text-tinta">{guarda.guarda_dias} dias</b>. Depois, o
              expurgo tira a conversa do banco e registra na trilha
              administrativa. O consumo do modelo continua na fatura.
            </p>
            <p className="text-[11px] text-tinta-3">
              Pedido de exclusão de uma pessoa (LGPD): peça ao suporte do
              Cockpit Rodovias — as conversas dela saem da base, com o motivo na
              trilha.
            </p>
          </div>
          <div className="grid content-start gap-2">
            <div className="flex flex-wrap items-end gap-2">
              <label className="grid gap-1 text-[11px] text-tinta-3">
                Prazo, em dias
                <input
                  aria-label="Prazo de guarda, em dias"
                  type="number"
                  inputMode="numeric"
                  min={guarda.minimo}
                  max={guarda.maximo}
                  value={dias}
                  onChange={(e) => definirDias(e.target.value)}
                  className={`${ENTRADA} w-28`}
                />
              </label>
              <Botao
                papel="primaria"
                disabled={!valido || numero === guarda.guarda_dias}
                onClick={() => void gravar()}
              >
                Gravar prazo
              </Botao>
            </div>
            <small className="text-[10.5px] text-tinta-3">
              De {guarda.minimo} dias a 2 anos ({guarda.maximo} dias); o padrão
              é {guarda.padrao}.
            </small>
            <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-[11px]">
              <dt className="text-tinta-3">Além do prazo agora</dt>
              <dd className="mono text-tinta">
                {guarda.alem_do_prazo.toLocaleString("pt-BR")} mensagens
              </dd>
              <dt className="text-tinta-3">A mais antiga</dt>
              <dd className="text-tinta">
                {guarda.mais_antiga
                  ? dataEHora(guarda.mais_antiga)
                  : "nenhuma conversa ainda"}
              </dd>
              <dt className="text-tinta-3">Último expurgo</dt>
              <dd className="text-tinta">
                {guarda.ultimo_expurgo
                  ? dataEHora(guarda.ultimo_expurgo)
                  : "nenhum ainda"}
              </dd>
            </dl>
            {aviso && (
              <p
                role={aviso.ok ? "status" : "alert"}
                className={`text-[11px] ${aviso.ok ? "text-verde" : "text-vermelho"}`}
              >
                {aviso.texto}
              </p>
            )}
          </div>
        </div>
      )}
    </Painel>
  );
}
